import {
  calcServiceMetrics,
  computeCostId,
  fetchApp,
  fetchDiskAttachments,
  fetchEndpointsByServiceId,
  fetchPersistentDisks,
  fetchRelease,
  fetchServicesByAppId,
  selectAppById,
  selectDiskStorageLabelByServiceId,
  selectEndpointsByServiceId,
  selectEnvironmentById,
  selectReleaseById,
  selectServiceById,
} from "@app/deploy";
import { findLoaderComposite } from "@app/loaders";
import { selectHasScheduledHas } from "@app/organizations";
import { useDispatch, useQuery, useSelector } from "@app/react";
import {
  appDetailUrl,
  appServiceDisksPathUrl,
  appServicePathMetricsUrl,
  appServiceScalePathUrl,
  appServiceScaleV2PathUrl,
  appServiceSettingsPathUrl,
  appServiceUrl,
  appServicesUrl,
  environmentAppsUrl,
  environmentDetailUrl,
} from "@app/routes";
import { schema } from "@app/schema";
import { setResourceStats } from "@app/search";
import type {
  DeployApp,
  DeployEndpoint,
  DeployEnvironment,
  DeployService,
} from "@app/types";
import { useEffect, useState } from "react";
import { Link, Outlet, useParams } from "react-router-dom";
import {
  COST_ESTIMATE_TOOLTIP_TEXT,
  CRUMB_VARIANTS,
  Code,
  DetailPageHeaderView,
  IconChevronDown,
  IconChevronRight,
  LoadingSpinner,
  PreCode,
  ResourceDetailBar,
  ResourceDetailItem,
  ResourceDetailsTable,
  ScaleRecsView,
  type TabItem,
  listToInvertedTextColor,
} from "../shared";
import { AppSidebarLayout } from "./app-sidebar-layout";

const getDeploymentStrategy = (
  service: DeployService,
  endpoints: DeployEndpoint[],
) => {
  if (endpoints.length > 0) {
    if (
      endpoints.some((e) =>
        ["http", "http_proxy_protocol", "grpc"].includes(e.type),
      )
    ) {
      return "Zero-Downtime";
    }
    return "Minimal-Downtime";
  }

  const strategies = [];
  if (service.forceZeroDowntime) {
    strategies.push("Zero-Downtime");
  } else {
    strategies.push("Zero-Overlap");
  }
  if (service.naiveHealthCheck) {
    strategies.push("Simple Healthcheck");
  }
  return strategies.join(", ");
};

// Read-only "Disk Storage" indicator for the Service detail header.
// Feature-gated by the caller, so this only fetches/reads when enabled. Loads
// the org-wide persistent-disk + attachment data and renders a summary line
// matching the Services-table cell logic:
//   - 0 active attachments -> "N/A"
//   - exactly 1            -> the disk handle
//   - 2+                   -> "{n} Attached"
const ServiceDiskStorageItem = ({ service }: { service: DeployService }) => {
  useQuery(fetchPersistentDisks());
  useQuery(fetchDiskAttachments());

  const label = useSelector((s) =>
    selectDiskStorageLabelByServiceId(s, { serviceId: service.id }),
  );

  return (
    <ResourceDetailItem title="Disk Storage">
      {service.id === "" ? <LoadingSpinner /> : (label ?? "N/A")}
    </ResourceDetailItem>
  );
};

export function ServiceHeader({
  app,
  service,
  endpoints,
  env,
  isLoading,
}: {
  app: DeployApp;
  service: DeployService;
  endpoints: DeployEndpoint[];
  env: DeployEnvironment;
  isLoading: boolean;
}) {
  // Query additional data that subpages need
  useQuery(fetchEndpointsByServiceId({ id: service.id }));

  const metrics = calcServiceMetrics(service);
  const [isOpen, setOpen] = useState(true);
  const deploymentStrategy = getDeploymentStrategy(service, endpoints);
  const cost = useSelector((s) =>
    schema.costs.selectById(s, { id: computeCostId("Service", service.id) }),
  );
  const release = useSelector((s) =>
    selectReleaseById(s, { id: service.currentReleaseId }),
  );

  return (
    <div className="flex flex-col gap-4">
      <ResourceDetailBar
        isLoading={isLoading}
        icon={
          <img
            src="/resource-types/logo-service.png"
            className="w-[32px] h-[32px]"
            aria-label="Service"
          />
        }
        nameLabel="Service"
        name={service.processType}
        id={service.id}
        dateLabel="Last Deploy"
        createdAt={release.id === "" ? null : release.createdAt}
        cost={cost.id === "" ? null : cost.estCost}
        costTooltip={COST_ESTIMATE_TOOLTIP_TEXT}
      />

      <ResourceDetailsTable columns={2}>
        <ResourceDetailItem title="Type" isLoading={service.id === ""}>
          <Code>{service.processType}</Code>
        </ResourceDetailItem>
        <ResourceDetailItem
          title="Container Count"
          isLoading={service.id === ""}
        >
          {service.containerCount}
        </ResourceDetailItem>
        <ResourceDetailItem title="Resource" isLoading={app.id === ""}>
          <Link to={appDetailUrl(app.id)}>{app.handle}</Link>
        </ResourceDetailItem>
        <ResourceDetailItem
          title="Container Size"
          isLoading={service.id === ""}
        >
          {`${metrics.containerSizeGB} GB`}
        </ResourceDetailItem>
        <ResourceDetailItem title="Environment" isLoading={env.id === ""}>
          <Link to={environmentDetailUrl(env.id)}>{env.handle}</Link>
        </ResourceDetailItem>
        <ResourceDetailItem title="CPU Share" isLoading={service.id === ""}>
          {metrics.cpuShare}
        </ResourceDetailItem>
        <ResourceDetailItem
          title="Deployment Strategy"
          isLoading={service.id === ""}
        >
          <Link to={appServiceSettingsPathUrl(app.id, service.id)}>
            {deploymentStrategy}
          </Link>
        </ResourceDetailItem>
        <ResourceDetailItem
          title="Container Profile"
          isLoading={service.id === ""}
        >
          {metrics.containerProfile.name}
        </ResourceDetailItem>
        <ServiceDiskStorageItem service={service} />
        <ResourceDetailItem
          title="Container Efficiency"
          isLoading={service.id === ""}
        >
          <ScaleRecsView service={service} />
        </ResourceDetailItem>
      </ResourceDetailsTable>
      {service.command ? (
        <div>
          <div className="-ml-2 flex justify-between items-center pt-1">
            <div className="flex flex-1">
              <div
                className="font-semibold flex items-center cursor-pointer"
                onClick={() => setOpen(!isOpen)}
                onKeyUp={() => setOpen(!isOpen)}
              >
                {isOpen ? (
                  <IconChevronDown variant="sm" />
                ) : (
                  <IconChevronRight variant="sm" />
                )}
                <p>Show Command</p>
              </div>
            </div>
          </div>
          {isOpen ? (
            <PreCode
              segments={listToInvertedTextColor(service.command.split(" "))}
              allowCopy
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ServicePageHeader() {
  const { id = "", serviceId = "" } = useParams();
  const dispatch = useDispatch();

  const loaderApp = useQuery(fetchApp({ id, withDeleted: true }));
  const loaderServices = useQuery(
    fetchServicesByAppId({ id: id, withDeleted: true }),
  );
  const app = useSelector((s) => selectAppById(s, { id }));
  const service = useSelector((s) => selectServiceById(s, { id: serviceId }));
  useQuery(fetchRelease({ id: service.currentReleaseId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByServiceId(s, { serviceId }),
  );
  const environment = useSelector((s) =>
    selectEnvironmentById(s, { id: app.environmentId }),
  );
  const loader = findLoaderComposite([loaderApp, loaderServices]);
  const hasScheduledHas = useSelector(selectHasScheduledHas);

  useEffect(() => {
    dispatch(setResourceStats({ id, type: "app", deletedAt: app?.deletedAt }));
  }, [app?.deletedAt]);

  const crumbs = [
    {
      name: environment.handle,
      to: environmentAppsUrl(environment.id),
      label: "Environment",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    },
    {
      name: app.handle,
      to: appServicesUrl(app.id),
      label: "App",
      crumbVariant: CRUMB_VARIANTS.UPPERCASE,
    },
  ];

  const tabs: TabItem[] = [
    { name: "Metrics", href: appServicePathMetricsUrl(id, serviceId) },
    { name: "Scale", href: appServiceScalePathUrl(id, serviceId) },
    ...(hasScheduledHas
      ? [
          {
            name: "Scale v2",
            href: appServiceScaleV2PathUrl(id, serviceId),
          },
        ]
      : []),
    { name: "Disk Storage", href: appServiceDisksPathUrl(id, serviceId) },
    { name: "Settings", href: appServiceSettingsPathUrl(id, serviceId) },
  ];

  return (
    <DetailPageHeaderView
      {...loader}
      breadcrumbs={crumbs}
      titleLabel="Service"
      title={serviceId ? service.processType : app.handle}
      detailsBox={
        <ServiceHeader
          app={app}
          service={service}
          endpoints={endpoints}
          env={environment}
          isLoading={loader.isLoading}
        />
      }
      tabs={tabs}
      lastBreadcrumbTo={appServiceUrl(app.id, service.id)}
    />
  );
}

export const ServiceDetailLayout = () => {
  return (
    <AppSidebarLayout header={<ServicePageHeader />}>
      <Outlet />
    </AppSidebarLayout>
  );
};
